import React, {FC, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {
  TrackDataEvent,
  useDataEventTracking,
} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  SimpleStepList,
  SingleStepList,
} from '../../../../../libs/shared/ui/src/components/steps/simpleStepsList';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {saveUserPreferencesAction} from '../../../../store/data/preferences/preferencesActions';
import {
  getActivationPreferences,
  getBasicsGuideIsCompleted,
} from '../../../../store/data/preferences/preferencesSelectors';
import {onboardingGuideSlideHeight} from '../../guide/onboardingGuideHelpers';
import {OnboardingGuideAnimatedStyles, OnboardingGuidePopover} from '../../guide/onboardingGuidePopover';
import {OnboardingGuideSlide} from '../../guide/onboardingGuideSlide';
import {startConnectChannelTaskAction} from '../../tasks/adminOnboardingTasks';
import {
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../../welcome/onboardingWelcomeHelpers';
import {OnboardingBasicsGuideVisual, OnboardingBasicsGuideVisualFrame} from './onboardingBasicsGuideVisual';

/*
 * Constants.
 */

enum BasicsGuideSlidesEnum {
  WHAT_IS_A_CHANNEL = 'what_is_a_channel',
  WHAT_IS_AN_INBOX = 'what_is_an_inbox',
  ROUTING_BETWEEN_INBOXES = 'routing_between_inboxes',
  INBOX_ORGANIZATION = 'inbox_organization',
  BEST_PRACTICES = 'best_practices',
  END_CTA = 'end_cta',
}

const slides = [
  BasicsGuideSlidesEnum.WHAT_IS_A_CHANNEL,
  BasicsGuideSlidesEnum.WHAT_IS_AN_INBOX,
  BasicsGuideSlidesEnum.ROUTING_BETWEEN_INBOXES,
  BasicsGuideSlidesEnum.INBOX_ORGANIZATION,
  BasicsGuideSlidesEnum.BEST_PRACTICES,
  BasicsGuideSlidesEnum.END_CTA,
] as const;

const steps = [
  {
    id: BasicsGuideSlidesEnum.WHAT_IS_A_CHANNEL,
    title: <Trans id="basics-guide-popover-step-title-what-is-a-channel">What is a “channel”?</Trans>,
  },
  {
    id: BasicsGuideSlidesEnum.WHAT_IS_AN_INBOX,
    title: <Trans id="basics-guide-popover-step-title-what-is-an-inbox">What is a Front “inbox”?</Trans>,
  },
  {
    id: BasicsGuideSlidesEnum.ROUTING_BETWEEN_INBOXES,
    title: (
      <Trans id="basics-guide-popover-step-title-routing-between-inboxes">
        Moving messages between inboxes
      </Trans>
    ),
  },
  {
    id: BasicsGuideSlidesEnum.INBOX_ORGANIZATION,
    title: (
      <Trans id="basics-guide-popover-step-title-inboxes-without-channels">Leveraging empty inboxes</Trans>
    ),
  },
  {
    id: BasicsGuideSlidesEnum.BEST_PRACTICES,
    title: (
      <Trans id="basics-guide-popover-step-title-best-practices">
        Learning best practices to organize inboxes
      </Trans>
    ),
  },
] as const;

type StepId = (typeof steps)[any]['id'];

const visualFrameBySlide: Record<BasicsGuideSlidesEnum, OnboardingBasicsGuideVisualFrame> = {
  [BasicsGuideSlidesEnum.WHAT_IS_A_CHANNEL]: OnboardingBasicsGuideVisualFrame.CHANNELS_INTRO,
  [BasicsGuideSlidesEnum.WHAT_IS_AN_INBOX]: OnboardingBasicsGuideVisualFrame.INBOXES,
  [BasicsGuideSlidesEnum.ROUTING_BETWEEN_INBOXES]: OnboardingBasicsGuideVisualFrame.ROUTING,
  [BasicsGuideSlidesEnum.INBOX_ORGANIZATION]: OnboardingBasicsGuideVisualFrame.INBOX_ORGANIZATION,
  [BasicsGuideSlidesEnum.BEST_PRACTICES]: OnboardingBasicsGuideVisualFrame.BEST_PRACTICES,
  [BasicsGuideSlidesEnum.END_CTA]: OnboardingBasicsGuideVisualFrame.IMPORT_MESSAGES,
};

const dataEventsBySlide: Record<
  BasicsGuideSlidesEnum,
  [
    OnboardingDataEventObject,
    OnboardingDataEventObject | null,
    OnboardingDataEventObject | null,
    OnboardingDataEventObject,
    OnboardingDataEventObject | null,
  ]
> = {
  [BasicsGuideSlidesEnum.WHAT_IS_A_CHANNEL]: [
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_A_CHANNEL_VIEW,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_A_CHANNEL_NEXT,
    null,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_A_CHANNEL_CANCEL,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_A_CHANNEL_STEP_LIST,
  ],
  [BasicsGuideSlidesEnum.WHAT_IS_AN_INBOX]: [
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_AN_INBOX_VIEW,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_AN_INBOX_NEXT,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_AN_INBOX_PREVIOUS,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_AN_INBOX_CANCEL,
    OnboardingDataEventObject.BASICS_GUIDE_WHAT_IS_AN_INBOX_STEP_LIST,
  ],
  [BasicsGuideSlidesEnum.ROUTING_BETWEEN_INBOXES]: [
    OnboardingDataEventObject.BASICS_GUIDE_ROUTING_BETWEEN_INBOXES_VIEW,
    OnboardingDataEventObject.BASICS_GUIDE_ROUTING_BETWEEN_INBOXES_NEXT,
    OnboardingDataEventObject.BASICS_GUIDE_ROUTING_BETWEEN_INBOXES_PREVIOUS,
    OnboardingDataEventObject.BASICS_GUIDE_ROUTING_BETWEEN_INBOXES_CANCEL,
    OnboardingDataEventObject.BASICS_GUIDE_ROUTING_BETWEEN_INBOXES_STEP_LIST,
  ],
  [BasicsGuideSlidesEnum.INBOX_ORGANIZATION]: [
    OnboardingDataEventObject.BASICS_GUIDE_INBOX_ORGANIZATION_VIEW,
    OnboardingDataEventObject.BASICS_GUIDE_INBOX_ORGANIZATION_NEXT,
    OnboardingDataEventObject.BASICS_GUIDE_INBOX_ORGANIZATION_PREVIOUS,
    OnboardingDataEventObject.BASICS_GUIDE_INBOX_ORGANIZATION_CANCEL,
    OnboardingDataEventObject.BASICS_GUIDE_INBOX_ORGANIZATION_STEP_LIST,
  ],
  [BasicsGuideSlidesEnum.BEST_PRACTICES]: [
    OnboardingDataEventObject.BASICS_GUIDE_BEST_PRACTICES_VIEW,
    OnboardingDataEventObject.BASICS_GUIDE_BEST_PRACTICES_NEXT,
    OnboardingDataEventObject.BASICS_GUIDE_BEST_PRACTICES_PREVIOUS,
    OnboardingDataEventObject.BASICS_GUIDE_BEST_PRACTICES_CANCEL,
    OnboardingDataEventObject.BASICS_GUIDE_BEST_PRACTICES_STEP_LIST,
  ],
  [BasicsGuideSlidesEnum.END_CTA]: [
    OnboardingDataEventObject.BASICS_GUIDE_END_CTA_VIEW,
    null,
    null,
    OnboardingDataEventObject.BASICS_GUIDE_END_CTA_CANCEL,
    null,
  ],
};

/*
 * Props.
 */

export interface BasicsGuidePopoverProps {
  panelId: AppPanelId;
  styles: OnboardingGuideAnimatedStyles | undefined;
  onClose: () => void;
  isBasicsGuideOpen: boolean;
}

/*
 * Styles.
 */

const StyledStepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StyledBasicsGuideStepsList = styled(SimpleStepList<StepId>)`
  width: 456px;
`;

const StyledNavDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const StyledCtaDetailsDiv = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 18px;
`;

/*
 * Component.
 */

export const BasicsGuidePopover: FC<BasicsGuidePopoverProps> = (props) => {
  const {onClose, isBasicsGuideOpen} = props;

  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const isBasicsGuideCompleted = useAppSelector(getBasicsGuideIsCompleted);
  const activationPreferences = useAppSelector(getActivationPreferences);
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [completedSlideIndexes, setCompletedSlideIndexes] = useState<Set<number>>(new Set([0]));
  const slide = slides[slideIndex];

  const [viewDataEvent, , , cancelDataEvent] = dataEventsBySlide[slide];
  useEffect(() => {
    if (!isBasicsGuideOpen) {
      return;
    }
    trackDataEvent(DataEventActionsEnum.VIEW, viewDataEvent);
  }, [isBasicsGuideOpen, trackDataEvent, viewDataEvent]);

  const onSaveCompletedProgress = () => {
    if (!isBasicsGuideCompleted) {
      dispatch(
        saveUserPreferencesAction({
          activationPreferences: {
            ...activationPreferences,
            setupGuideTasks: [
              ...new Set([...(activationPreferences.setupGuideTasks ?? []), 'inbox-vs-channel-basics-guide']),
            ],
          },
        }),
      );
    }
  };

  const onConnectAChannel = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.BASICS_GUIDE_DONE);
    setSlideIndex(0);
    dispatch(
      saveUserPreferencesAction({
        activationPreferences: {
          ...activationPreferences,
          setupGuideTasks: [
            ...new Set([...(activationPreferences.setupGuideTasks ?? []), 'knows-setup-guide-exists-in-p0']),
          ],
        },
      }),
    );
    dispatch(startConnectChannelTaskAction());
    onClose();
  };

  const onCancel = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, cancelDataEvent);
    setSlideIndex(0);
    onClose();
  };

  const isLastSlide = slideIndex === slides.length - 1;

  return (
    <OnboardingGuidePopover styles={props.styles} onRequestClose={onCancel}>
      <OnboardingGuideSlide
        height={onboardingGuideSlideHeight}
        navPrimary={renderNav(
          slideIndex,
          setSlideIndex,
          setCompletedSlideIndexes,
          onSaveCompletedProgress,
          onConnectAChannel,
          onCancel,
          trackDataEvent,
        )}
        isLeftContent
      >
        <StyledStepsContainer>
          <StyledOnboardingFormTitlesDiv>
            <StyledOnboardingFormTitleDiv>
              {isLastSlide ? (
                <Trans id="basics-guide-popover-title-cta">
                  It’s time to connect a channel to import team messages 🎉
                </Trans>
              ) : (
                <Trans id="basics-guide-popover-title">Understand Front inboxes vs. channels</Trans>
              )}
            </StyledOnboardingFormTitleDiv>
          </StyledOnboardingFormTitlesDiv>
          {isLastSlide ? (
            <StyledCtaDetailsDiv>
              <Trans id="basics-guide-popover-details-cta">
                Now that you’ve learned about Front inboxes and channels, connect a channel to start managing
                your team’s messages.
              </Trans>
            </StyledCtaDetailsDiv>
          ) : (
            <StyledBasicsGuideStepsList
              steps={computeSteps(slideIndex, completedSlideIndexes)}
              size={VisualSizesEnum.SMALL}
              onStepClick={(stepId) => {
                const stepSlideIndex = slides.indexOf(stepId);
                if (stepSlideIndex >= 0) {
                  const stepSlide = slides[stepSlideIndex];
                  const [, , , , stepListDataEvent] = dataEventsBySlide[stepSlide];
                  if (stepListDataEvent) {
                    trackDataEvent(DataEventActionsEnum.CLICK, stepListDataEvent);
                  }
                  setSlideIndex(stepSlideIndex);
                }
              }}
            />
          )}
        </StyledStepsContainer>
      </OnboardingGuideSlide>
      <OnboardingBasicsGuideVisual frame={visualFrameBySlide[slide]} />
    </OnboardingGuidePopover>
  );
};

function renderNav(
  slideIndex: number,
  setSlideIndex: (computeSlideIndex: (currentSlideIndex: number) => number) => void,
  setCompletedSlideIndexes: (
    computeCompletedSlideIndexes: (currentCompletedSlideIndexes: Set<number>) => Set<number>,
  ) => void,
  onSaveCompletedProgress: () => void,
  onConnectAChannel: () => void,
  onCancel: () => void,
  trackDataEvent: TrackDataEvent,
) {
  const slide = slides[slideIndex];
  const [, nextDataEvent, previousDataEvent] = dataEventsBySlide[slide];
  const isFirstSlide = slideIndex === 0;
  const isLastSlide = slideIndex === slides.length - 1;
  const isSecondToLastSlide = slideIndex === slides.length - 2;
  return (
    <StyledNavDiv>
      <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.MEDIUM} onClick={onCancel}>
        <Trans id="basics-guide-popover-nav-cancel">Cancel</Trans>
      </Button>
      {!isFirstSlide && !isLastSlide && (
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => {
            if (previousDataEvent) {
              trackDataEvent(DataEventActionsEnum.CLICK, previousDataEvent);
            }
            setSlideIndex((currentSlideIndex) => {
              if (currentSlideIndex === 0) {
                return currentSlideIndex;
              }

              return currentSlideIndex - 1;
            });
          }}
        >
          <Trans id="basics-guide-popover-nav-previous">Previous</Trans>
        </Button>
      )}
      {!isLastSlide && (
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => {
            if (nextDataEvent) {
              trackDataEvent(DataEventActionsEnum.CLICK, nextDataEvent);
            }

            if (isSecondToLastSlide) {
              onSaveCompletedProgress();
            }

            setSlideIndex((currentSlideIndex) => {
              if (currentSlideIndex === slides.length - 1) {
                return currentSlideIndex;
              }

              return currentSlideIndex + 1;
            });
            setCompletedSlideIndexes(
              (currentCompletedSlideIndexes) => new Set([...currentCompletedSlideIndexes, slideIndex + 1]),
            );
          }}
        >
          {isSecondToLastSlide ? (
            <Trans id="basics-guide-popover-nav-done">Done</Trans>
          ) : (
            <Trans id="basics-guide-popover-nav-next">Next</Trans>
          )}
        </Button>
      )}
      {isLastSlide && (
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => onConnectAChannel()}
        >
          <Trans id="basics-guide-popover-nav-cta">Connect a channel</Trans>
        </Button>
      )}
    </StyledNavDiv>
  );
}

function computeSteps(slideIndex: number, completedSlideIndexes: Set<number>): SingleStepList<StepId> {
  const stepsWithState = steps.map((step, stepIndex) => ({
    ...step,
    isCompleted: completedSlideIndexes.has(stepIndex),
    isSelected: stepIndex === slideIndex,
    isDisabled: stepIndex > slideIndex && !completedSlideIndexes.has(stepIndex),
  }));

  return stepsWithState;
}
